/*
 * VOVIA Free Gift Progress — controller
 * -------------------------------------
 * Persistent script (loaded once per page, survives drawer re-renders).
 * Responsibilities:
 *   1. Reconcile the cart with the configured milestones: auto-add a gift
 *      when its threshold is reached, auto-remove it when the cart drops
 *      below the threshold again. Idempotent + loop-safe.
 *   2. Animate the progress bar smoothly across drawer re-renders (the
 *      drawer replaces its innerHTML on cart:refresh, which would
 *      otherwise make the bar "jump").
 *   3. Multi-currency: thresholds are configured in the shop currency
 *      (EUR). For shoppers browsing in another presentment currency the
 *      server-rendered bar is re-computed client-side with the exact
 *      storefront FX rate (Shopify.currency.rate) — the same rate Shopify
 *      uses to convert the automatic discounts' minimum amounts. The
 *      converted threshold is rounded UP to a whole currency unit so the
 *      bar can never claim "unlocked" before the discount actually applies.
 *   4. Respect the shopper: if they manually remove a gift we added, we
 *      don't force it back during this browsing session.
 *
 * Config comes from the #vovia-gift-progress-config JSON (theme.liquid),
 * generated from Theme settings → "Free gift progress".
 */
(function () {
  'use strict';
  if (window.__voviaGiftProgressInit) return;
  window.__voviaGiftProgressInit = true;

  var GIFT_PROP = '_vovia_gift';
  var S = {
    busy: false,
    pending: false,
    failed: {},        // variantId -> true (add failed, e.g. sold out) — don't retry-loop
    lastEligible: {},  // variantId -> bool from previous reconcile (for dismissal detection)
    hadGift: {},       // variantId -> bool: a _vovia_gift line existed on the previous reconcile
    prevFill: {},      // context -> last rendered fill %
    lastCart: null,    // most recent /cart.js payload
    reachedPrev: null,     // milestone reached-flags from the previous render (unlock detection)
    celebrateQueue: [],    // milestone indexes waiting for their unlock moment
    celebrateActive: null  // { index, until } — the one celebration currently showing
  };

  function getConfig() {
    var el = document.getElementById('vovia-gift-progress-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function currencyRate() {
    try {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        var r = parseFloat(window.Shopify.currency.rate);
        if (r > 0) return r;
      }
    } catch (e) {}
    return 1;
  }

  // Shop-currency cents -> presentment cents. For foreign currencies we round
  // UP to a whole currency unit: a tiny safety margin so the bar never shows
  // a milestone as reached while Shopify's converted discount minimum isn't.
  function toPresentment(cents, rate) {
    if (rate === 1) return cents;
    return Math.ceil((cents * rate) / 100) * 100;
  }

  function formatMoney(cents) {
    try {
      if (typeof window.formatMoney === 'function') {
        var d = document.createElement('div');
        d.innerHTML = window.formatMoney(cents);
        var t = d.textContent.trim();
        if (t) return t;
      }
    } catch (e) {}
    try {
      var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e2) {
      return '€' + (cents / 100).toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function dismissedSet() {
    try { return JSON.parse(sessionStorage.getItem('vgp:dismissed') || '[]'); } catch (e) { return []; }
  }
  function saveDismissed(list) {
    try { sessionStorage.setItem('vgp:dismissed', JSON.stringify(list)); } catch (e) {}
  }
  function isDismissed(variantId) { return dismissedSet().indexOf(variantId) !== -1; }
  function dismiss(variantId) {
    var list = dismissedSet();
    if (list.indexOf(variantId) === -1) { list.push(variantId); saveDismissed(list); }
  }
  function undismiss(variantId) {
    var list = dismissedSet();
    var i = list.indexOf(variantId);
    if (i !== -1) { list.splice(i, 1); saveDismissed(list); }
  }

  function fetchCart() {
    return fetch('/cart.js', { cache: 'no-store', headers: { 'X-VGP': '1' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { S.lastCart = cart; return cart; });
  }

  function isGiftLine(item) {
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  // Subtotal basis: line prices after line-level discounts, excluding our gift lines.
  function computeBasis(cart) {
    var basis = 0;
    (cart.items || []).forEach(function (item) {
      if (!isGiftLine(item)) basis += item.final_line_price;
    });
    return basis;
  }

  /* ---- shared progress math (mirrors the Liquid snippet) ----------- */

  function computeState(cart, config, rate) {
    var basis = computeBasis(cart);
    var n = config.milestones.length;
    var fill = 0, prevAmount = 0, prevPos = 0;
    var nextRemaining = null, allDone = true;
    var reached = [];

    for (var i = 0; i < n; i++) {
      var amt = toPresentment(config.milestones[i].cents, rate);
      var pos = ((2 * (i + 1) - 1) * 100) / (2 * n);
      var hit = basis >= amt;
      reached.push(hit);
      if (hit) {
        fill = pos; prevAmount = amt; prevPos = pos;
      } else if (allDone) {
        allDone = false;
        nextRemaining = amt - basis;
        var span = amt - prevAmount;
        if (span > 0) fill = prevPos + ((basis - prevAmount) / span) * (pos - prevPos);
      }
    }
    if (allDone) fill = 100;
    fill = Math.max(0, Math.min(100, Math.round(fill * 10) / 10));
    return { basis: basis, fill: fill, remaining: nextRemaining, allDone: allDone, reached: reached };
  }

  /* ---- reconcile: auto add/remove gifts ----------------------------- */

  function reconcile() {
    if (S.busy) { S.pending = true; return; }
    S.busy = true;
    _reconcile()
      .catch(function (e) { try { console.warn('[vgp]', e); } catch (_) {} })
      .then(function () {
        S.busy = false;
        if (S.pending) { S.pending = false; reconcile(); }
        onRenderSoon();
      });
  }

  async function _reconcile() {
    var config = getConfig();
    if (!config || !config.enabled || !config.milestones || !config.milestones.length) return;

    var rate = currencyRate();
    var cart = await fetchCart();
    var basis = computeBasis(cart);

    var toAdd = [];
    var toRemove = [];
    var validVariants = {};

    config.milestones.forEach(function (m) {
      var threshold = toPresentment(m.cents, rate);
      var eligible = basis >= threshold && threshold > 0;
      validVariants[m.variantId] = true;

      var giftLines = cart.items.filter(function (i) { return i.variant_id === m.variantId && isGiftLine(i); });
      var anyLine = cart.items.some(function (i) { return i.variant_id === m.variantId; });

      if (eligible) {
        // A gift line existed before and vanished while still eligible → shopper
        // removed it on purpose. (Requires hadGift so a paid line of the same
        // variant, or a failed add, can never trigger a false dismissal.)
        if (S.lastEligible[m.variantId] && S.hadGift[m.variantId] && !giftLines.length) dismiss(m.variantId);

        if (!anyLine && !S.failed[m.variantId] && !isDismissed(m.variantId)) {
          toAdd.push(m.variantId);
        }
      } else {
        undismiss(m.variantId); // dropping below the threshold resets the dismissal
        giftLines.forEach(function (line) { toRemove.push(line.key); });
      }
      S.lastEligible[m.variantId] = eligible;
      S.hadGift[m.variantId] = giftLines.length > 0;
    });

    // Also clean up gift-flagged lines that no longer match any configured milestone.
    cart.items.forEach(function (i) {
      if (isGiftLine(i) && !validVariants[i.variant_id] && toRemove.indexOf(i.key) === -1) {
        toRemove.push(i.key);
      }
    });

    if (!toAdd.length && !toRemove.length) return;

    if (toAdd.length) {
      var res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-VGP': '1' },
        body: JSON.stringify({
          items: toAdd.map(function (id) {
            var props = {};
            props[GIFT_PROP] = '1';
            return { id: id, quantity: 1, properties: props };
          })
        })
      });
      if (!res.ok) {
        // Batch failed (e.g. one variant sold out) — retry one by one, remember failures.
        for (var k = 0; k < toAdd.length; k++) {
          var props2 = {};
          props2[GIFT_PROP] = '1';
          var single = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-VGP': '1' },
            body: JSON.stringify({ id: toAdd[k], quantity: 1, properties: props2 })
          });
          if (!single.ok) S.failed[toAdd[k]] = true;
        }
      }
    }

    for (var j = 0; j < toRemove.length; j++) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-VGP': '1' },
        body: JSON.stringify({ id: toRemove[j], quantity: 0 })
      });
    }

    // Tell the theme to re-render (drawer, cart page section, header count).
    var fresh = await fetchCart();
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { cart: fresh }
    }));
  }

  var debounceTimer;
  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 150);
  }

  /* ---- FX correction: fix up the server-rendered (EUR) bar ---------- */

  function fxPatch() {
    var rate = currencyRate();
    if (rate === 1) return; // shop currency: server-rendered values are exact

    var config = getConfig();
    if (!config || !config.milestones || !config.milestones.length) return;

    var roots = document.querySelectorAll('[data-vgp]');
    if (!roots.length) return;

    if (!S.lastCart) { fetchCart().then(function () { onRenderSoon(); }); return; }

    var st = computeState(S.lastCart, config, rate);
    var sig = st.basis + '|' + st.fill + '|' + st.allDone + '|' + st.reached.join('');

    roots.forEach(function (root) {
      if (root.__vgpFxSig === sig) return;
      root.__vgpFxSig = sig;

      // Head text
      var span = root.querySelector('.vgp__head span');
      if (span) {
        if (st.allDone) {
          span.textContent = config.textDone || 'All free gifts unlocked!';
        } else {
          var tpl = esc(config.textProgress || 'Just [amount] more to unlock your next free gift');
          span.innerHTML = tpl.replace('[amount]', '<strong data-vgp-remaining>' + esc(formatMoney(st.remaining)) + '</strong>');
        }
      }
      root.classList.toggle('vgp--done', st.allDone);

      // Fill (data-progress drives the animation pass below)
      var fillEl = root.querySelector('.vgp-fill');
      if (fillEl) fillEl.setAttribute('data-progress', String(st.fill));

      // Stops + labels
      var stops = root.querySelectorAll('.vgp__stop');
      var labels = root.querySelectorAll('.vgp__label');
      st.reached.forEach(function (hit, i) {
        if (stops[i]) stops[i].classList.toggle('vgp__stop--reached', hit);
        if (labels[i]) labels[i].classList.toggle('vgp__label--reached', hit);
      });

      // Milestone amounts under the labels (converted thresholds)
      var amts = root.querySelectorAll('[data-vgp-amt]');
      config.milestones.forEach(function (m, i) {
        if (amts[i]) amts[i].textContent = formatMoney(toPresentment(m.cents, rate));
      });
    });
  }

  /* ---- unlock celebration: subtle pulse + pop + toast ---------------- */

  function labelText(el) {
    if (!el) return 'Gift';
    var node = el.childNodes[0];
    var t = node && node.nodeType === 3 ? node.textContent.trim() : '';
    return t || 'Gift';
  }

  function celebrateCheck() {
    var config = getConfig();
    if (!config || !config.milestones || !config.milestones.length || !S.lastCart) return;

    var st = computeState(S.lastCart, config, currencyRate());

    if (!S.reachedPrev) {
      // First observation of this page load: set the baseline, never celebrate.
      S.reachedPrev = st.reached.slice();
      return;
    }

    st.reached.forEach(function (hit, i) {
      var isActive = S.celebrateActive && S.celebrateActive.index === i;
      if (hit && !S.reachedPrev[i] && S.celebrateQueue.indexOf(i) === -1 && !isActive) {
        S.celebrateQueue.push(i);
      }
    });
    S.reachedPrev = st.reached.slice();
    // Drop queued celebrations for milestones that un-reached in the meantime
    // (e.g. the shopper removed the item again before their toast played).
    S.celebrateQueue = S.celebrateQueue.filter(function (i) { return st.reached[i]; });
    pumpCelebrations();
    applyCelebration();
  }

  // One celebration at a time: toasts queue up instead of overlapping when
  // two milestones unlock in the same cart update.
  function pumpCelebrations() {
    if (S.celebrateActive || !S.celebrateQueue.length) return;
    var i = S.celebrateQueue.shift();
    S.celebrateActive = { index: i, until: Date.now() + 2300 };
    applyCelebration();
    setTimeout(endCelebration, 2300);
  }

  // Idempotent: re-applies the active celebration after every drawer re-render.
  function applyCelebration() {
    var a = S.celebrateActive;
    if (!a) return;
    var i = a.index;

    document.querySelectorAll('[data-vgp]').forEach(function (root) {
      var stop = root.querySelectorAll('.vgp__stop')[i];
      var bar = root.querySelector('.vgp__bar');
      if (!stop || !bar) return;

      stop.classList.add('vgp__stop--celebrate');

      var toast = bar.querySelector('.vgp__toast');
      var label = labelText(root.querySelectorAll('.vgp__label')[i]);
      var msg = '🎁 ' + label + ' unlocked!';
      if (!toast) {
        toast = document.createElement('span');
        toast.className = 'vgp__toast';
        toast.textContent = msg;
        bar.appendChild(toast);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { toast.classList.add('vgp__toast--show'); });
        });
      } else if (toast.textContent !== msg) {
        toast.textContent = msg;
      }
    });
  }

  function endCelebration() {
    S.celebrateActive = null;
    document.querySelectorAll('.vgp__stop--celebrate').forEach(function (el) {
      el.classList.remove('vgp__stop--celebrate');
    });
    document.querySelectorAll('.vgp__toast').forEach(function (t) {
      t.classList.remove('vgp__toast--show');
      setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 320);
    });
    // Short breather, then show the next queued unlock (if any).
    setTimeout(pumpCelebrations, 300);
  }

  /* ---- smooth fill animation across re-renders ---------------------- */

  function animateFills() {
    var fills = document.querySelectorAll('.vgp-fill[data-progress]');
    fills.forEach(function (el) {
      var target = parseFloat(el.getAttribute('data-progress')) || 0;
      if (el.__vgpTarget === target) return;
      el.__vgpTarget = target;

      var root = el.closest('[data-vgp]');
      var ctx = (root && root.getAttribute('data-vgp-context')) || 'drawer';
      var prev = S.prevFill[ctx];

      if (typeof prev === 'number' && Math.abs(prev - target) > 0.1) {
        el.style.transition = 'none';
        el.style.width = prev + '%';
        void el.offsetWidth; // force reflow, then animate to the real value
        el.style.transition = '';
        requestAnimationFrame(function () { el.style.width = target + '%'; });
      } else {
        el.style.width = target + '%';
      }
      S.prevFill[ctx] = target;
    });
  }

  var renderTimer;
  function onRenderSoon() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(function () {
      fxPatch();          // correct amounts/fill for foreign currencies first
      animateFills();     // then animate from the previous width to the target
      celebrateCheck();   // detect newly reached milestones → pulse + toast
    }, 30);
  }

  /* ---- triggers ------------------------------------------------------ */

  // The theme dispatches cart:refresh after its own cart operations.
  document.documentElement.addEventListener('cart:refresh', function (e) {
    if (e && e.detail && e.detail.cart) S.lastCart = e.detail.cart;
    // Our own dispatch also lands here; reconcile is idempotent so a
    // second pass is a cheap no-op (it exits before any mutation).
    schedule();
    onRenderSoon();
  });

  // Belt & braces: catch any cart mutation done via fetch (product forms, apps).
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url) || '';
    var ours = init && init.headers && (init.headers['X-VGP'] || (typeof init.headers.get === 'function' && init.headers.get('X-VGP')));
    var p = origFetch.apply(this, arguments);
    if (!ours && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
      p.then(function () { schedule(); }).catch(function () {});
    }
    return p;
  };

  var observer = new MutationObserver(function () { onRenderSoon(); });

  function boot() {
    observer.observe(document.body, { childList: true, subtree: true });
    onRenderSoon();
    schedule(); // initial reconcile on page load
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
